import { useMemo, useState, useCallback, type RefObject } from "react";

import { Search, X } from "lucide-react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import "../Relationship/account-relationships/relationship-prototype-skin.css";

import LevelMixStrip from "./LevelMixStrip";
import { clusterFromAccountHealth } from "./portfolioClusterStyles";
import PortfolioMapAccountPreviewModal from "./PortfolioMapAccountPreviewModal";
import WorkingSetBanner from "./WorkingSetBanner";
import { useHealthScoreThresholds } from "../../Hooks/useHealthScoreThresholds";
import type { PortfolioAccountRow } from "../../Store/Reducers/MyPortfolioOverviewSlice";
import {
  isAccountTeamRole,
  isRole,
  ROLES,
} from "../../Utils/Constants/RoleConstants";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../Utils/Helper/LocalStorage";
import { PortfolioOverviewTab } from "../Relationship/portfolio-overview/PortfolioOverviewTab";

interface Props {
  accounts: PortfolioAccountRow[];
  levelMix: {
    al: {
      label: string;
      values: Record<string, number>;
      counts?: Record<string, number>;
      total: number;
    }[];
    rl: {
      label: string;
      values: Record<string, number>;
      counts?: Record<string, number>;
      total: number;
      unassessed_count?: number | null;
    }[];
  };
  loading: boolean;
  withOrg: (path: string) => string;
  headerFiltersRef?: RefObject<HTMLDivElement | null>;
  onGoToAccounts?: () => void;
  onGoToRelationships?: () => void;
  onOpenAssess?: (account: { id: string; name: string }) => void;
  workingSetAccountIds?: string[] | null;
  onClearWorkingSet?: () => void;
}

const alRowToStripKey = (alRow: string | number): string => {
  if (alRow === "unknown") return "unk";
  return `al${alRow}`;
};

const rlColumnToStripKey = (column: string | number): string => {
  if (column === "unknown") return "unassessed";
  const rl = Number(column);
  if (!Number.isFinite(rl)) return "unassessed";
  if (rl <= 1) return "weak";
  if (rl <= 3) return "moderate";
  return "strong";
};

const PortfolioGoalsTab = ({
  accounts,
  levelMix,
  loading,
  withOrg,
  headerFiltersRef,
  onGoToAccounts,
  onGoToRelationships,
  onOpenAssess,
  workingSetAccountIds,
  onClearWorkingSet,
}: Props) => {
  const navigate = useNavigate();
  const { accountHealth: ahThresholds } = useHealthScoreThresholds();

  const [previewAccount, setPreviewAccount] = useState<any>(null);
  const [showPercentage, setShowPercentage] = useState(false);
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  /** Hide Primary/Secondary/Executive ring legend for Account Team only. */
  const showSponsorRoleLegend = !isAccountTeamRole(loggedInUser?.role);
  /** Executive login: all bubbles use dashed Executive ring (not P/S assignment). */
  const forceExecutiveRing = isRole(loggedInUser?.role, ROLES.EXECUTIVE);
  const [highlightAlKey, setHighlightAlKey] = useState<string | null>(null);
  const [highlightRlKey, setHighlightRlKey] = useState<string | null>(null);
  const [activeAlAxis, setActiveAlAxis] = useState<string | number | null>(
    null
  );
  const [activeRlAxis, setActiveRlAxis] = useState<string | number | null>(
    null
  );
  const [showRlNudge, setShowRlNudge] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const scopedAccounts = useMemo(() => {
    if (!workingSetAccountIds) return accounts;
    const ids = new Set(workingSetAccountIds);
    return accounts.filter(a => ids.has(a.customer_id));
  }, [accounts, workingSetAccountIds]);

  const gridAccounts = useMemo(
    () =>
      scopedAccounts.map(a => ({
        id: a.customer_id,
        name: a.customer_name,
        alLevel: a.al_level,
        rlAggregate: a.rl_aggregate,
        tier: a.tier,
        industry: a.industry,
        region: a.region,
        cluster: clusterFromAccountHealth(
          a.account_health,
          a.customer_id,
          ahThresholds
        ),
        accountHealth: a.account_health,
        businessHealth: a.business_health,
        revenue: a.revenue,
        renewalDate: a.renewal_date,
        renewalDays: a.renewal_days,
        mappedPairs: a.mapped_pairs,
        assessedPairs: a.assessed_pairs,
        pendingPairs: a.pending_pairs,
        pendingAssessments: a.pending_pairs,
        mappingPossiblePairs: a.total_possible_pairs,
        totalPossiblePairs: a.total_possible_pairs,
        needsAttention: a.needs_attention,
        attentionReasons: a.attention_reasons,
        roleType: a.role_type,
      })),
    [scopedAccounts, ahThresholds]
  );

  const handleOpenPreview = useCallback((account: any) => {
    setPreviewAccount(account);
  }, []);

  const handleAlAxisClick = useCallback((alRow: string | number) => {
    const key = alRowToStripKey(alRow);
    setHighlightRlKey(null);
    setActiveRlAxis(null);
    setShowRlNudge(false);
    setActiveAlAxis(prev => {
      const clearing = prev != null && String(prev) === String(alRow);
      setHighlightAlKey(clearing ? null : key);
      return clearing ? null : alRow;
    });
    document
      .getElementById("al-mix-strip")
      ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, []);

  const handleRlAxisClick = useCallback((column: string | number) => {
    const key = rlColumnToStripKey(column);
    setHighlightAlKey(null);
    setActiveAlAxis(null);
    setActiveRlAxis(prev => {
      const clearing = prev != null && String(prev) === String(column);
      setHighlightRlKey(clearing ? null : key);
      setShowRlNudge(!clearing);
      return clearing ? null : column;
    });
    document
      .getElementById("rl-mix-strip")
      ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, []);

  const goToAccountSynopsis = useCallback(() => {
    if (!previewAccount?.id) return;
    navigate(withOrg(`/account-synopsis/${previewAccount.id}`), {
      state: {
        from: withOrg("/my-portfolio-accounts"),
        fromLabel: "My Accounts",
      },
    });
    setPreviewAccount(null);
  }, [navigate, previewAccount, withOrg]);

  const goToAccountRelationships = useCallback(() => {
    if (!previewAccount?.id) return;
    navigate(
      withOrg(
        `/relationship?tab=account-relationships&accountId=${encodeURIComponent(previewAccount.id)}`
      )
    );
    setPreviewAccount(null);
  }, [navigate, previewAccount, withOrg]);

  const goToMyRelationships = useCallback(() => {
    navigate(withOrg("/relationship?tab=my-relationships"));
    setPreviewAccount(null);
  }, [navigate, withOrg]);

  const openAssessFromPreview = useCallback(() => {
    if (!previewAccount?.id || !onOpenAssess) return;
    onOpenAssess({
      ...previewAccount,
      id: previewAccount.id,
      name: previewAccount.name || "Account",
    });
    setPreviewAccount(null);
  }, [onOpenAssess, previewAccount]);

  const headerFiltersPortal = headerFiltersRef?.current
    ? createPortal(
        <div className="rl-prototype-skin flex min-w-0 flex-wrap items-center justify-end gap-2 sm:gap-3">
          <label className="inline-flex cursor-pointer select-none items-center gap-2 text-[11px] font-medium text-gray-500">
            <span>Show percentage</span>
            <button
              type="button"
              role="switch"
              aria-checked={showPercentage}
              aria-label="Show percentage"
              onClick={() => setShowPercentage(v => !v)}
              className={`relative h-4 w-7 shrink-0 rounded-full border-none transition-colors ${
                showPercentage ? "bg-gray-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform ${
                  showPercentage ? "left-3.5" : "left-0.5"
                }`}
              />
            </button>
          </label>
          <div className="acct-picker-search pg-controls-search !mb-0 !min-w-[160px] !max-w-[220px] !flex-none">
            <Search className="acct-picker-search-icon" aria-hidden />
            <input
              type="search"
              role="searchbox"
              className="acct-picker-search-input"
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
              aria-label="Search accounts on portfolio grid"
            />
            {searchTerm ? (
              <button
                type="button"
                className="acct-picker-search-clear"
                onClick={() => setSearchTerm("")}
                aria-label="Clear search"
              >
                <X size={14} aria-hidden />
              </button>
            ) : null}
          </div>
        </div>,
        headerFiltersRef.current
      )
    : null;

  if (loading) {
    return (
      <>
        {headerFiltersPortal}
        <div className="flex min-h-[320px] flex-col items-center justify-center text-gray-500">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-purple-600" />
          <p className="mt-2">Loading portfolio map...</p>
        </div>
      </>
    );
  }

  if (accounts.length === 0) {
    return (
      <>
        {headerFiltersPortal}
        <div className="flex min-h-[320px] flex-col items-center justify-center text-gray-500">
          <p className="text-lg font-medium text-gray-700">
            No accounts in your portfolio
          </p>
          <p className="mt-1 text-sm">
            Accounts you are assigned to will appear here.
          </p>
        </div>
      </>
    );
  }

  return (
    <div className="rl-prototype-skin">
      {headerFiltersPortal}

      {workingSetAccountIds && onClearWorkingSet && (
        <WorkingSetBanner
          count={workingSetAccountIds.length}
          onClear={onClearWorkingSet}
        />
      )}

      <LevelMixStrip
        alRows={levelMix.al}
        rlRows={levelMix.rl}
        highlightAlKey={highlightAlKey}
        highlightRlKey={highlightRlKey}
        showRlNudge={showRlNudge}
        showPercentage={showPercentage}
        onGoToAccounts={onGoToAccounts}
        onGoToRelationships={onGoToRelationships}
      />

      <PortfolioOverviewTab
        accounts={gridAccounts}
        onOpenAccountPreview={handleOpenPreview}
        portfolioGridOwnerLabel="Your"
        hideViewToggle
        hideSearchControls
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        showSponsorRoleLegend={showSponsorRoleLegend}
        forceExecutiveRing={forceExecutiveRing}
        onAlAxisClick={handleAlAxisClick}
        onRlAxisClick={handleRlAxisClick}
        activeAlAxis={activeAlAxis}
        activeRlAxis={activeRlAxis}
        enableZoneOverlay
      />

      <PortfolioMapAccountPreviewModal
        open={Boolean(previewAccount)}
        account={previewAccount}
        onClose={() => setPreviewAccount(null)}
        onGoAccountSynopsis={goToAccountSynopsis}
        onGoAccountRelationships={goToAccountRelationships}
        onGoMyRelationships={goToMyRelationships}
        onOpenAssess={onOpenAssess ? openAssessFromPreview : undefined}
      />
    </div>
  );
};

export default PortfolioGoalsTab;
